import { DietaryRow } from '@fusion/dietary/row'
import { alpha, Box, Card, CircularProgress, Stack as MuiStack, Typography } from '@mui/material'

import type React from 'react'
import { lazy, memo, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { MenuItem } from '../../domain/menu-item'
import type { MenuVendor } from '../../domain/menu-vendor'
import { getMenuItemTranslatedValue } from '../../utils/getMenuItemTranslatedValue'
import { ItemPrice } from '../menu-item-card/item-price'
import { MenuItemImage } from '../menu-item-card/menu-item-image'
import { PopularBadge } from '../menu-item-card/popular-badge'
import { ServesLabel } from '../menu-item-card/serves-label'
import { useMenuItemCard } from '../menu-item-card/useMenuItemCard'
import { PopularItemActions } from './popular-item-actions'

const MenuItemModal = lazy(() =>
  import('../menu-item-card/menu-item-modal').then((module) => ({ default: module.MenuItemModal })),
)

export type Props = {
  item: MenuItem
  vendor: MenuVendor
  taxAcronym: string
}

const TITLE_LINES = 2
const TITLE_LINE_HEIGHT = 1.35
const TITLE_FONT_SIZE = '0.9375rem'

const PopularItemCard: React.FC<Props> = ({ item, vendor, taxAcronym }) => {
  const { t, i18n } = useTranslation()
  const currency = vendor?.currency || 'GBP'
  const [modalIsOpen, toggleModalIsOpen] = useState(false)
  const {
    quantity,
    account,
    servesAmount,
    price,
    priceCaveat,
    quickAddDisabled,
    handleQuantityIncrease,
    handleQuantityDecrease,
    handleAddItemToBasket,
    handleAddToCart,
    minimumQuantity,
  } = useMenuItemCard(item, vendor, { addSource: 'PopularItemsCarousel' })

  const locale = i18n.language?.split('-')[0]?.toLowerCase()
  const itemName = getMenuItemTranslatedValue({
    translations: item.translations,
    locale,
    key: 'name',
    fallback: item.name,
  })

  const shoppingCartHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (item.options && item.options.length > 0) {
      toggleModalIsOpen(true)
    } else {
      handleAddToCart(e)
    }
  }

  return (
    <>
      {modalIsOpen && (
        <Suspense
          fallback={
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1300,
              }}>
              <CircularProgress />
            </Box>
          }>
          <MenuItemModal
            initialQty={quantity}
            taxAcronym={taxAcronym}
            mode="add"
            menuItem={item}
            vendor={vendor}
            onConfirmClick={handleAddItemToBasket}
            onClose={() => toggleModalIsOpen(false)}
            accountName={account?.name}
          />
        </Suspense>
      )}

      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRadius: 2,
          border: (theme) => `2px solid ${theme.palette.primary.main}`,
          boxShadow: 'none',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: (theme) =>
            theme.transitions.create(['transform', 'box-shadow'], {
              duration: theme.transitions.duration.shorter,
            }),
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: (theme) => `0 6px 16px -6px ${alpha(theme.palette.primary.main, 0.55)}`,
          },
        }}
        onClick={() => toggleModalIsOpen(true)}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            flexShrink: 0,
          }}>
          <MenuItemImage
            src={item.image}
            alt={itemName || ''}
            sizes="(max-width: 600px) 80vw, 260px"
            sx={{ borderRadius: 0 }}
          />
          <PopularBadge
            itemId={item._id}
            sx={{ position: 'absolute', top: 8, left: 8, zIndex: 3 }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            gap: 1,
            p: 1.5,
          }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: TITLE_FONT_SIZE,
              lineHeight: TITLE_LINE_HEIGHT,
              height: `${TITLE_LINES * TITLE_LINE_HEIGHT}em`,
              display: '-webkit-box',
              WebkitLineClamp: TITLE_LINES,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}>
            {itemName}
          </Typography>

          <MuiStack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            {item.dietaries && item.dietaries.length > 0 && (
              <DietaryRow
                variant="compact"
                renderOverflow={({ count }) => (
                  <Typography variant="body3" textAlign="center">
                    +{count}
                  </Typography>
                )}
                dietaries={item.dietaries}
                allergens={item.allergens || []}
                maxToShow={4}
              />
            )}
            {servesAmount && <ServesLabel servesAmount={servesAmount} />}
          </MuiStack>

          <ItemPrice
            currency={currency}
            price={price}
            priceCaveat={priceCaveat}
            taxAcronym={taxAcronym}
            compact
            align="left"
            inline
          />

          <Box sx={{ mt: 'auto', pt: 0.5 }}>
            <PopularItemActions
              quantity={quantity}
              minimumQuantity={minimumQuantity}
              addToCartDisabled={quickAddDisabled}
              onDecrease={handleQuantityDecrease}
              onIncrease={handleQuantityIncrease}
              onAddToCart={shoppingCartHandler}
            />
          </Box>
        </Box>
      </Card>
    </>
  )
}

export const MemoizedPopularItemCard = memo(
  PopularItemCard,
  (prevProps, nextProps) =>
    prevProps.item._id === nextProps.item._id &&
    prevProps.vendor.id === nextProps.vendor.id &&
    prevProps.taxAcronym === nextProps.taxAcronym,
)

MemoizedPopularItemCard.displayName = 'MemoizedPopularItemCard'

export { PopularItemCard }
